import { AlertTriangleIcon } from 'lucide-react';

interface OrphanBannerProps {
  /**
   * ISO-8601 timestamp at which the GitLab project was deleted upstream
   * (`hill.externalProjectDeletedAt`), or `null` if the project still exists.
   * The banner is a no-op when null, so callers can drop the prop check.
   */
  externalProjectDeletedAt: string | null;
  /**
   * `hill.completedAt`. The banner is suppressed once the hill is Done -
   * completion already locks the hill in read-only mode and the orphan
   * warning would compete for attention with the Done badge for no extra
   * value. Pass `null` for live hills.
   */
  completedAt: string | null;
}

/**
 * Warning banner for active hills whose GitLab project has been deleted
 * upstream.
 *
 * The marker is only ever stamped by `performResync`, which refuses legacy
 * `provider: 'linear'` hills outright, so an orphaned hill is always a GitLab
 * one and the copy can name it without a provider branch.
 *
 * Renders only when:
 *  - `externalProjectDeletedAt !== null` (the upstream project is gone), AND
 *  - `completedAt === null` (the hill is still Active - a Done hill is
 *    already explicitly archived and doesn't need a second warning).
 *
 * The hill itself survives in this orphaned state by design: archives are
 * the canonical record per CLAUDE.md, and we don't want an upstream deletion
 * to silently erase team history. The user reads this banner and decides:
 * Mark the hill as Done (preserving the snapshot timeline) or delete it
 * outright.
 */
export function OrphanBanner({ externalProjectDeletedAt, completedAt }: OrphanBannerProps) {
  if (externalProjectDeletedAt === null || completedAt !== null) return null;

  return (
    <output
      role="alert"
      data-testid="orphan-banner"
      className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900"
    >
      <AlertTriangleIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="text-sm">
        <p className="font-medium">GitLab project deleted</p>
        <p className="mt-0.5 text-amber-800 text-xs">
          The GitLab project backing this hill has been removed. You can mark this hill as Done to
          preserve its history in the Archive, or delete it from the hills list.
        </p>
      </div>
    </output>
  );
}
